import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';
import { getApexReference, jobErrorMessage } from '../lib/jobReference';

export const JobEmailComposer = ({ job, onClose, onSent }) => {
  const [form, setForm] = useState({ to: '', subject: `it IN A bit — ${getApexReference(job) || job.job_type}`, message: '' });
  const [context, setContext] = useState(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState(null);
  const requestId = useRef(crypto.randomUUID());
  const loadContext = useCallback(async () => {
    setError('');
    try { const { data } = await axios.get(`${API}/appointments/${job.id}/email-context`); setContext(data); setForm(f => ({ ...f, to: f.to || data.to })); }
    catch (err) { setError(jobErrorMessage(err, 'Unable to load email settings.')); }
  }, [job.id]);
  useEffect(() => { loadContext(); }, [loadContext]);
  useEffect(() => {
    if (!pending) return;
    let cancelled = false; let timer; let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/appointments/${job.id}/emails`);
        if (cancelled) return;
        const email = data.find(e => e.id === pending);
        if (email?.status === 'accepted') { toast.success('Email sent — accepted by mail server'); setPending(null); setSending(false); onSent(); return; }
        if (email?.status === 'failed') { setError(email.error || 'Email could not be sent.'); setPending(null); setSending(false); requestId.current = crypto.randomUUID(); return; }
      } catch { /* Keep checking the same request; never automatically resend. */ }
      if (cancelled) return;
      if (++attempts >= 45) { setError('Sending has not been confirmed. Check email history before sending again.'); setSending(false); setPending(null); return; }
      timer = setTimeout(poll, 1000);
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [pending, job.id, onSent]);
  const change = e => { setForm({ ...form, [e.target.name]: e.target.value }); requestId.current = crypto.randomUUID(); };
  const send = async e => {
    e.preventDefault(); setSending(true); setError('');
    try {
      const { data } = await axios.post(`${API}/appointments/${job.id}/emails`, { ...form, request_id: requestId.current });
      setPending(data.id);
    } catch (err) { setError(jobErrorMessage(err, 'Sending could not be confirmed. Check email history before retrying.')); setSending(false); }
  };
  return <section data-testid="job-email-composer" className="job-email-composer">
    <h3 className="text-lg font-bold">Email customer</h3>
    <p className="text-sm text-white/70 mt-1" data-testid="job-email-from">From: it IN A bit{context?.from_email ? ` <${context.from_email}>` : ''}</p>
    {context && !context.smtp_configured && <p role="alert" data-testid="job-email-smtp-warning" className="job-action-error">SMTP is not configured. Configure email in Admin settings before sending.</p>}
    <form onSubmit={send} className="space-y-4 mt-4">
      <label className="block">To<input data-testid="job-email-to" name="to" type="email" required className="retro-input w-full mt-1" value={form.to} onChange={change} disabled={sending} /></label>
      {context?.source && <p data-testid="job-email-source" className="text-xs text-white/60">From {context.source.toLowerCase()}</p>}
      <label className="block">Subject<input data-testid="job-email-subject" name="subject" required maxLength={200} className="retro-input w-full mt-1" value={form.subject} onChange={change} disabled={sending} /></label>
      <label className="block">Message<textarea data-testid="job-email-message" name="message" required maxLength={20000} rows={6} className="retro-input w-full mt-1" value={form.message} onChange={change} disabled={sending} /></label>
      {error && <div role="alert" data-testid="job-email-error" className="job-action-error">{error}{!context && <button type="button" data-testid="job-email-retry-context" onClick={loadContext}>Retry</button>}</div>}
      <div className="flex flex-wrap gap-3">
        <button type="submit" data-testid="job-email-send" className="retro-btn inline-flex items-center gap-2" disabled={sending || !context?.smtp_configured}><Send size={16} />{sending ? 'Sending…' : 'Send email'}</button>
        <button type="button" data-testid="job-email-cancel" className="retro-btn" onClick={onClose} disabled={sending}>Cancel</button>
      </div>
    </form>
  </section>;
};